import React, { useEffect, useState } from "react";
import { api, apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatGs, LOCATION_NAMES, formatDate } from "@/lib/format";
import { PageHeader, Btn } from "@/components/ui/kit";
import { toast } from "sonner";
import { Clock, Phone, User, Package } from "lucide-react";

const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-amber-500/10 text-amber-500 border-amber-500/30" },
  confirmado: { label: "Confirmado", cls: "bg-blue-500/10 text-blue-500 border-blue-500/30" },
  listo: { label: "Listo", cls: "bg-primary/10 text-primary border-primary/30" },
  entregado: { label: "Entregado", cls: "bg-emerald-500/10 text-emerald-500 border-emerald-500/30" },
  cancelado: { label: "Cancelado", cls: "bg-red-500/10 text-red-500 border-red-500/30" },
};
const FLOW = { pendiente: "confirmado", confirmado: "listo", listo: "entregado" };

export default function OnlineOrders() {
  const { location } = useAuth();
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/orders", { params: { location, status: filter || undefined } }); setOrders(data); }
    catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [location, filter]);

  const setStatus = async (o, status) => {
    try { await api.put(`/orders/${o.id}/status`, { status }); toast.success("Pedido actualizado"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Pedidos Online" subtitle={`Pedidos para retirar en ${LOCATION_NAMES[location]}`}>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="order-filter" className="h-10 px-3 rounded-lg bg-secondary border border-border focus:outline-none">
          <option value="">Todos</option>
          {Object.keys(STATUS).map((s) => <option key={s} value={s}>{STATUS[s].label}</option>)}
        </select>
      </PageHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {orders.map((o) => (
          <div key={o.id} className="card-surface p-5 flex flex-col" data-testid={`order-${o.id}`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-data text-xs text-muted-foreground">#{o.id.slice(0, 8)}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS[o.status]?.cls}`}>{STATUS[o.status]?.label}</span>
            </div>
            <div className="flex items-center gap-2 text-sm mb-1"><User className="w-4 h-4 text-primary" /> {o.customer_name}</div>
            {o.customer_phone && <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1"><Phone className="w-4 h-4" /> {o.customer_phone}</div>}
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3"><Clock className="w-4 h-4" /> Retiro: {o.pickup_time} · {formatDate(o.timestamp)}</div>
            <div className="space-y-1 border-t border-border pt-3 mb-3 flex-1">
              {o.items.map((i, idx) => (
                <div key={idx} className="flex justify-between text-sm"><span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-muted-foreground" />{i.name} x{i.quantity}</span><span className="font-data">{formatGs(i.price * i.quantity)}</span></div>
              ))}
              {o.notes && <p className="text-xs text-muted-foreground italic mt-2">Nota: {o.notes}</p>}
            </div>
            <div className="flex items-center justify-between">
              <span className="font-head font-bold">Total: <span className="font-data text-primary">{formatGs(o.total)}</span></span>
            </div>
            {FLOW[o.status] && (
              <div className="flex gap-2 mt-3">
                <Btn onClick={() => setStatus(o, FLOW[o.status])} data-testid={`advance-${o.id}`} className="flex-1 h-9">
                  {o.status === "pendiente" ? "Confirmar" : o.status === "confirmado" ? "Marcar listo" : "Entregar"}
                </Btn>
                {o.status === "pendiente" && <Btn variant="danger" onClick={() => setStatus(o, "cancelado")} data-testid={`cancel-${o.id}`} className="h-9">Cancelar</Btn>}
              </div>
            )}
          </div>
        ))}
        {orders.length === 0 && <div className="col-span-full card-surface p-12 text-center text-muted-foreground">No hay pedidos online</div>}
      </div>
    </div>
  );
}
